/**
 * Minimal arithmetic evaluator for security expressions (spreads, ratios, …).
 *
 * Replaces `mathjs` (1.7 MB of source in the main bundle) for the only grammar the app
 * produces: numbers, identifiers resolved from a scope, `+ - * / % ^`, unary minus,
 * parentheses and a few named functions (`strike(security)` for option filters plus the
 * usual Math helpers). `^` is exponentiation and binds tighter than unary minus, and the
 * scope lookup throws on unknown symbols, matching mathjs semantics for these inputs.
 */

type Scope = Record<string, unknown>

type Node =
    | { t: 'num'; v: number }
    | { t: 'id'; n: string }
    | { t: 'neg'; a: Node }
    | { t: 'bin'; op: '+' | '-' | '*' | '/' | '%' | '^'; a: Node; b: Node }
    | { t: 'call'; n: string; args: Node[] }

type Token =
    | { t: 'num'; v: number }
    | { t: 'id'; n: string }
    | { t: 'op'; v: string }
    | { t: 'eof' }

const IDENT_START = /[A-Za-z_$]/
const IDENT_PART = /[A-Za-z0-9_$]/
const DIGIT = /[0-9]/

const toNumber = (v: unknown, what: string): number => {
    if (typeof v === 'number') return v
    if (typeof v === 'boolean') return v ? 1 : 0
    if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) return Number(v)
    throw new Error(`Cannot convert ${what} to a number`)
}

const asSecurity = (v: unknown): { strikePrice?: number; priceMultiplier?: number } => {
    if (v && typeof v === 'object') return v as { strikePrice?: number; priceMultiplier?: number }
    throw new Error('strike() expects a security')
}

/** Functions callable from an expression. Keep in sync with `availableFilters` in util/math.ts. */
export const EXPRESSION_FUNCTIONS: Record<string, (...args: unknown[]) => number> = {
    strike: (sec) => {
        const s = asSecurity(sec)
        return (s.strikePrice || 0) / (s.priceMultiplier || 100)
    },
    abs: (x) => Math.abs(toNumber(x, 'abs argument')),
    sqrt: (x) => Math.sqrt(toNumber(x, 'sqrt argument')),
    round: (x) => Math.round(toNumber(x, 'round argument')),
    floor: (x) => Math.floor(toNumber(x, 'floor argument')),
    ceil: (x) => Math.ceil(toNumber(x, 'ceil argument')),
    exp: (x) => Math.exp(toNumber(x, 'exp argument')),
    log: (x) => Math.log(toNumber(x, 'log argument')),
    log10: (x) => Math.log10(toNumber(x, 'log10 argument')),
    min: (...xs) => Math.min(...xs.map((x) => toNumber(x, 'min argument'))),
    max: (...xs) => Math.max(...xs.map((x) => toNumber(x, 'max argument')))
}

function tokenize(src: string): Token[] {
    const tokens: Token[] = []
    let i = 0
    while (i < src.length) {
        const c = src[i]
        if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
            i++
            continue
        }
        if (DIGIT.test(c) || (c === '.' && DIGIT.test(src[i + 1] ?? ''))) {
            let j = i
            while (j < src.length && (DIGIT.test(src[j]) || src[j] === '.')) j++
            if ((src[j] === 'e' || src[j] === 'E') && /[0-9+-]/.test(src[j + 1] ?? '')) {
                j++
                if (src[j] === '+' || src[j] === '-') j++
                while (j < src.length && DIGIT.test(src[j])) j++
            }
            const text = src.slice(i, j)
            const v = Number(text)
            if (isNaN(v)) throw new Error(`Invalid number "${text}"`)
            tokens.push({ t: 'num', v })
            i = j
            continue
        }
        if (IDENT_START.test(c)) {
            let j = i + 1
            while (j < src.length && IDENT_PART.test(src[j])) j++
            tokens.push({ t: 'id', n: src.slice(i, j) })
            i = j
            continue
        }
        if ('+-*/%^(),'.includes(c)) {
            tokens.push({ t: 'op', v: c })
            i++
            continue
        }
        throw new Error(`Unexpected character "${c}" at ${i}`)
    }
    tokens.push({ t: 'eof' })
    return tokens
}

function parse(src: string): Node {
    const tokens = tokenize(src)
    let pos = 0
    const peek = () => tokens[pos]
    const isOp = (v: string) => {
        const tk = tokens[pos]
        return tk.t === 'op' && tk.v === v
    }
    const expect = (v: string) => {
        if (!isOp(v)) throw new Error(`Expected "${v}"`)
        pos++
    }

    // additive := multiplicative (('+' | '-') multiplicative)*
    const additive = (): Node => {
        let node = multiplicative()
        while (isOp('+') || isOp('-')) {
            const op = (tokens[pos++] as { v: '+' | '-' }).v
            node = { t: 'bin', op, a: node, b: multiplicative() }
        }
        return node
    }
    // multiplicative := unary (('*' | '/' | '%') unary)*
    const multiplicative = (): Node => {
        let node = unary()
        while (isOp('*') || isOp('/') || isOp('%')) {
            const op = (tokens[pos++] as { v: '*' | '/' | '%' }).v
            node = { t: 'bin', op, a: node, b: unary() }
        }
        return node
    }
    // unary := ('-' | '+') unary | power      (so -2^2 === -(2^2), as in mathjs)
    const unary = (): Node => {
        if (isOp('-')) {
            pos++
            return { t: 'neg', a: unary() }
        }
        if (isOp('+')) {
            pos++
            return unary()
        }
        return power()
    }
    // power := primary ('^' unary)?           (right associative, exponent may carry a sign)
    const power = (): Node => {
        const base = primary()
        if (isOp('^')) {
            pos++
            return { t: 'bin', op: '^', a: base, b: unary() }
        }
        return base
    }
    const primary = (): Node => {
        const tk = peek()
        if (tk.t === 'num') {
            pos++
            return { t: 'num', v: tk.v }
        }
        if (tk.t === 'id') {
            pos++
            if (isOp('(')) {
                pos++
                const args: Node[] = []
                if (!isOp(')')) {
                    args.push(additive())
                    while (isOp(',')) {
                        pos++
                        args.push(additive())
                    }
                }
                expect(')')
                return { t: 'call', n: tk.n, args }
            }
            return { t: 'id', n: tk.n }
        }
        if (isOp('(')) {
            pos++
            const inner = additive()
            expect(')')
            return inner
        }
        throw new Error(tk.t === 'eof' ? 'Unexpected end of expression' : `Unexpected token "${(tk as { v: string }).v}"`)
    }

    const root = additive()
    if (peek().t !== 'eof') throw new Error('Unexpected trailing input')
    return root
}

function evalNode(node: Node, scope: Scope): unknown {
    switch (node.t) {
        case 'num':
            return node.v
        case 'id': {
            if (!Object.prototype.hasOwnProperty.call(scope, node.n)) throw new Error(`Undefined symbol ${node.n}`)
            return scope[node.n]
        }
        case 'neg':
            return -toNumber(evalNode(node.a, scope), 'operand')
        case 'call': {
            const fn = EXPRESSION_FUNCTIONS[node.n]
            if (!fn) throw new Error(`Undefined function ${node.n}`)
            return fn(...node.args.map((a) => evalNode(a, scope)))
        }
        case 'bin': {
            const a = toNumber(evalNode(node.a, scope), 'operand')
            const b = toNumber(evalNode(node.b, scope), 'operand')
            switch (node.op) {
                case '+': return a + b
                case '-': return a - b
                case '*': return a * b
                case '/': return a / b
                case '%': return a % b
                case '^': return Math.pow(a, b)
            }
        }
    }
}

const MAX_CACHED = 256
const parsed = new Map<string, Node>()

/** Parse once per distinct expression string (candle evaluation calls this per bar and per OHLC key). */
function compile(expression: string): Node {
    let node = parsed.get(expression)
    if (!node) {
        node = parse(expression)
        if (parsed.size >= MAX_CACHED) parsed.delete(parsed.keys().next().value as string)
        parsed.set(expression, node)
    }
    return node
}

/** Evaluate `expression` with identifiers resolved from `scope`; throws on unknown symbols or bad syntax. */
export function evaluateExpression(expression: string, scope: Scope = {}): number {
    const value = evalNode(compile(expression), scope)
    return toNumber(value, 'result')
}
